import { connect } from 'pwa-helpers';
import { html } from "lit";
import { customElement, property, query, state } from 'lit/decorators.js';
import '../context/context';
import store from '../../store/store';
import { BaseComponent } from '../../features/base-component/base-component';
import { getUserDataForUserAnalytics, selectUser } from '../user-profile/user-profile.selectors';
import { StoreState } from '../../store/store.interface';
import { getCartPageUrl } from '../../common/utils/cart-util';
import { getShareCartId } from './share-cart.selectors';
import { ActionStatus } from '../../common/constants/action-status';
import { fetchShareCartId } from './share-cart.thunks';
import emit from '../../common/events';
import { getCart } from '../cart/shop-cart/store/shop-cart.selectors';
import { getCartAnalyticsOverrides } from '../cart/shop-cart/shop-cart.analytics';

/**
 * An element to show share cart link.
 */
@customElement('mhecomm-share-cart')
export class ShareCart extends connect(store)(BaseComponent) {

  /**
   *  Share cart button text
   */
  @property()
  text = 'Share';

  /**
   * Share cart modal descrption for instruction about share cart link.
   */
  @property()
  shareCartModalDescription = 'Click "Copy Link" below to share your cart. Product ISBNs, quantities, and prices will be shared to the link\'s recipient.'

  @state()
  private shareCartTooltip = 'Copy to clipboard';

  @state()
  private shareCartId: string | undefined;

  @state()
  private copyButtonText: string;

  @state()
  private copyButtonDisabled = false;

  @state()
  private showSuccessMessage = false;

  @state()
  private closingModal = false;

  @query('#shareCartLink')
  shareCartLinkInput!: HTMLInputElement;

  private modal: any;

  private openerEl: HTMLElement | null = null;

  private readonly SHARE_CART_ID = 'shareCartId';

  private readonly CID_PARAM = 'cid';

  private readonly defaultCopyButtonText = "Copy Link";

  private readonly shareCartModalTitle = 'Share My Cart';

  private readonly loadingText = "Loading...";

  private readonly linkCopySuccessMessage = "Link copied successfully!";

  private readonly shareLinkLabel = "Shared Link URL:";

  constructor() {
    super();
    this.copyButtonText = this.defaultCopyButtonText;
  }

  updated() {
    this.modal = this.bsInit('#shareCartModal', window.bootstrap?.Modal)?.instance;
  }

  renderComp() {
    return html`
    <mhecomm-context inverselogic display="empty-cart">
    <div slot="main">  
      <button aria-haspopup="dialog" aria-controls="shareCartModal" class="btn btn-link me-2" @click=${(e: Event) => this.shareMyCart(e)}>${this.text}</button>
      <div
        class="modal fade"
        id="shareCartModal"
        @hidden.bs.modal=${() => this._onModalHidden()}
        tabindex="-1"
        aria-hidden="true"
        aria-labelledby="shareCartModalTitle">
        <div class="modal-dialog">
          <div class="modal-content">
            <div class="modal-header">
              <h2 class="h5 modal-title" id="shareCartModalTitle">${this.shareCartModalTitle}</h2>
              <button   
                type="button"
                class="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
                @click=${() => this.onCloseModal()}
              ></button>
            </div>
            <div class="modal-body">
              ${this.showSuccessMessage? html`
              <div class="alert alert-dismissible alert-success" role="alert">
                <p class="mb-0">
                ${this.uxtIconReady ? html`<uxt-icon icon="check-circle"></uxt-icon>` : ''}
                  <span>&nbsp;</span><span>${this.linkCopySuccessMessage}</span>
                </p>
              </div>
              ` : html``}
              <p>${this.shareCartModalDescription}</p>
              <div class="mb-3 row">
              <label for="shareCartLink" class="col-sm-12 form-label fw-semibold">${this.shareLinkLabel}</label>
              <div class="col-sm-12">
                  <textarea
                      readonly
                      class="form-control-plaintext"
                      id="shareCartLink"
                      rows="3"
                      .value=${this.getShareCartUrl()}
                  ></textarea>
                  <br>
                  <button type="button" class="btn btn-primary" id="copyShareCartLink" data-bs-toggle="tooltip"
                      data-bs-placement="top" data-bs-html="true" @click="${this.copyShareCart}" data-bs-original-title="${this.shareCartTooltip}" 
                      title="${this.shareCartTooltip}" ?disabled=${this.copyButtonDisabled}>${this.copyButtonText}</button>
              </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </mhecomm-context>
    `;
  }

  getShareCartUrl() {
    if (this.shareCartId) {
      const shareCartUrl = new URL(getCartPageUrl(selectUser(store.getState() as StoreState)));
      shareCartUrl.searchParams.set(this.SHARE_CART_ID, this.shareCartId);
      shareCartUrl.searchParams.set(this.CID_PARAM, "eml|CORP|SharedCart|email|clicks|");
      return decodeURI(shareCartUrl.toString());
    } else {
      return "...";
    }
  }

  stateChanged(state: StoreState) {
    this.shareCartId = getShareCartId(state)
    if (state.shareCartState.status == ActionStatus.succeeded) {
      this.copyButtonText = this.defaultCopyButtonText;
      this.copyButtonDisabled = false;
    } else if (state.shareCartState.status == ActionStatus.loading) {
      this.copyButtonText = this.loadingText;
      this.copyButtonDisabled = true;
    }
  }

  private shareMyCart(e?: Event) {
    // remember opener so we can restore focus when the modal closes
    this.openerEl = (e?.currentTarget as HTMLElement) || this.openerEl || null;
    store.dispatch(fetchShareCartId());
    this.showSuccessMessage = false;
    emit({
      element: this,
      name: 'open-share-my-cart-modal',
      detail: this.dataLayerDetail(),
    });
    this.modal.show();
  }

  onCloseModal(){
    // the closingModal is a sanity check to ensure this emit only happens once.
    if (this.closingModal) {
      return;
    }
    this.closingModal = true;
    emit({
      element: this,
      name: 'close-share-my-cart-modal',
      detail: this.dataLayerDetail(),
    });
    setTimeout(() => {
      this.modal.hide();
      this.closingModal = false;
    }, 500);
  }

  private _onModalHidden() {
    try {
      if (this.openerEl && typeof this.openerEl.focus === 'function') {
        requestAnimationFrame(() => { this.openerEl!.focus(); });
      }
    } finally {
      this.closingModal = false;
    }
  }

  private copyShareCart() {
    this.shareCartLinkInput.select();
    this.shareCartLinkInput.setSelectionRange(0, 99999);
    this.copyTextToClipboard(this.shareCartLinkInput.value);

    this.showSuccessMessage = true;
    emit({
      element: this,
      name: 'copy-share-my-cart-link',
      detail: this.dataLayerDetail(),
    });
  }

  private copyTextToClipboard(text: string) {
    navigator.clipboard.writeText(text);
  }

  private dataLayerDetail() {
    const state = store.getState() as StoreState;
    const cart = getCart(state);
    return {
      label: this.text,
      productList: cart?.products,
      url: window.location.href,
      user: getUserDataForUserAnalytics(state),
      ...getCartAnalyticsOverrides(state),
    };
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-share-cart': ShareCart;
  }
}
